import { useCallback } from "react";
import mixpanel from "mixpanel-browser";
import { useEffect, useRef } from "react";
import { useFetchBusinessProfilesQuery, useGetInfoQuery } from "../api/dataApi";
import Cookies from "js-cookie";
import Clarity from "@microsoft/clarity";

const useMixpanel = () => {
  const { data: userInfo } = useGetInfoQuery();
  const { data: businessProfiles } = useFetchBusinessProfilesQuery();

  const hasIdentified = useRef(false);

  useEffect(() => {
    const userId = userInfo?.data?.id;

    if (
      userId &&
      typeof userId === "string" &&
      userId.trim() !== "" &&
      !hasIdentified.current
    ) {
      mixpanel.identify(userId);

      try {
        Clarity.identify(userId);
        Clarity.setTag("Custom user ID", userId);
      } catch (error) {
        console.error(error);
      }

      hasIdentified.current = true;
    }
  }, [userInfo?.data?.id]);

  const trackEvent = useCallback((eventName, data, isProduct) => {
    const queryParams = new URLSearchParams(window.location.search);

    try {
      Clarity.event(eventName);
    } catch (error) {
      console.error(error);
    }

    // UTM parameters are only read from URL, not from cookies
    const getUtmParam = (queryKey) => {
      return queryParams.get(queryKey) || null;
    };

    const direction = businessProfiles?.data?.direction;

    mixpanel.track(eventName, {
      ...(getUtmParam("utm_source") && {
        web_utm_source: getUtmParam("utm_source"),
      }),
      ...(getUtmParam("utm_campaign") && {
        web_utm_campaign: getUtmParam("utm_campaign"),
      }),
      ...(getUtmParam("utm_medium") && {
        web_utm_medium: getUtmParam("utm_medium"),
      }),
      ...(getUtmParam("utm_content") && {
        web_utm_content: getUtmParam("utm_content"),
      }),
      ...(getUtmParam("utm_term") && {
        web_utm_term: getUtmParam("utm_term"),
      }),
      ...(localStorage.getItem("signupMethod") && {
        web_entrance:
          localStorage.getItem("signupMethod") === "Login" ? "login" : "signup",
      }),
      ...(localStorage.getItem("web_booster_pay")
        ? { web_level: "GROWTH" }
        : { web_level: "TRIAL" }),
      plan_name: "2-Week Trial, 4-Week Plan, 8-Week Plan",
      ...(isProduct && { product_type: direction?.toLowerCase() }),
      ...data,
      isDebug: false,
      funnel: process.env.REACT_APP_FUNNEL,
    });
  }, []);

  const trackP1Event = useCallback((eventName, data) => {
    const queryParams = new URLSearchParams(window.location.search);

    try {
      Clarity.event(eventName);
    } catch (error) {
      console.error(error);
    }

    const getParam = (cookieKey, queryKey) => {
      const cookieVal = Cookies.get(cookieKey);
      const queryVal = queryParams.get(queryKey);
      return cookieVal || queryVal || null;
    };

    // UTM parameters are only read from URL, not from cookies
    const getUtmParam = (queryKey) => {
      return queryParams.get(queryKey) || null;
    };

    mixpanel.track(eventName, {
      ...data,
      ...(getUtmParam("utm_source") && {
        web_utm_source: getUtmParam("utm_source"),
      }),
      ...(getUtmParam("utm_campaign") && {
        web_utm_campaign: getUtmParam("utm_campaign"),
      }),
      ...(getUtmParam("utm_medium") && {
        web_utm_medium: getUtmParam("utm_medium"),
      }),
      ...(getUtmParam("utm_content") && {
        web_utm_content: getUtmParam("utm_content"),
      }),
      ...(getUtmParam("utm_term") && {
        web_utm_term: getUtmParam("utm_term"),
      }),
      ...(eventName === "web_price" && {
        plan_name: localStorage.getItem("plan"),
      }),
      ...(localStorage.getItem("session_id") && {
        session_id: localStorage.getItem("session_id"),
      }),
      ...(localStorage.getItem("signupMethod") && {
        web_entrance:
          localStorage.getItem("signupMethod") === "Login" ? "login" : "signup",
      }),
      ...(localStorage.getItem("userId") && {
        user_id: localStorage.getItem("userId"),
      }),
      ...(Date.now() && { timestamp: Date.now() }),
      isDebug: false,
      funnel: process.env.REACT_APP_FUNNEL,
    });
  }, []);

  return { trackP1Event, trackEvent, businessProfiles };
};

export default useMixpanel;
